import React, { useState, useContext, useEffect, useRef } from "react";
import { useToast } from "../../hooks/useToast";

import {
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
} from "@material-ui/core";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import ShowTicketOpen from "../ShowTicketOpenModal";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import StandardModal from "../ui/StandardModal";
import useTicketTabs from "../../hooks/useTicketTabs";

const useStyles = makeStyles((theme) => {
  return {
  autoComplete: {
    width: 300,
  },
  maxWidth: {
    width: "100%",
  },
  buttonColorError: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
  },
  formControl: {
    margin: theme.spacing(1, 0),
    minWidth: "100%",
  },
  selectMenu: {
    height: 40,
    minHeight: 40,
    borderRadius: 8,
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: 8,
    },
    "&.MuiOutlinedInput-root": {
      fontSize: "0.9rem",
      "&:hover fieldset": {
        borderColor: theme.palette.primary.light,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  menuItem: {
    fontSize: "0.9rem",
    height: 36,
    minHeight: 36,
    paddingTop: 4,
    paddingBottom: 4,
  },
  inputLabel: {
    fontSize: "0.9rem",
    transform: "translate(14px, 12px) scale(1)",
    "&.MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.75)",
    },
  },
};
});

const AcceptTicketWithouSelectQueue = ({
  modalOpen,
  onClose,
  ticketId,
  ticket,
  onTicketAccepted,
}) => {
  const classes = useStyles();
  const toast = useToast();
  const [selectedQueue, setSelectedQueue] = useState("");
  const [selectedTicketTab, setSelectedTicketTab] = useState("");
  const [loading, setLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");
  const { tabs: ticketTabs } = useTicketTabs({ skipCounts: true });
  const { setTabOpen, setCurrentTicket } = useContext(TicketsContext);
  const isMounted = useRef(true);

  const { get: getSetting } = useCompanySettings();

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    try {
      if (user.queues.length === 1) {
        setSelectedQueue(user.queues[0].id);
      }
    } catch (err) {
      if (isMounted.current) {
        setLoading(false);
        toastError(err);
      }
    }
  }, [selectedQueue, user.queues]);

  useEffect(() => {
    if (modalOpen && ticket?.contact?.ticketTabId && ticketTabs.length > 0) {
      const contactTab = ticketTabs.find(t => t.id === ticket.contact.ticketTabId);
      if (contactTab) {
        setSelectedTicketTab(contactTab.id);
      }
    } else if (modalOpen && ticketTabs.length > 0) {
      const defaultTab = ticketTabs.find(t => t.isDefault);
      if (defaultTab) {
        setSelectedTicketTab(defaultTab.id);
      }
    }
  }, [modalOpen, ticket, ticketTabs]);

  const handleClose = () => {
    if (!isMounted.current) return;
    onClose();
    setSelectedQueue("");
    setSelectedTicketTab("");
  };

  const handleCloseAlert = () => {
    if (!isMounted.current) return;
    setOpenAlert(false);
    setLoading(false);
  };

  const handleSendMessage = async (id) => {
    if (!isMounted.current) return;

    let isGreetingMessage = false;

    try {
      const setting = await getSetting({
        column: "sendGreetingAccepted",
      });
      if (setting.sendGreetingAccepted === "enabled") isGreetingMessage = true;
    } catch (err) {
      if (isMounted.current) toastError(err);
      return;
    }

    if (!isGreetingMessage) return;

    let settingMessage;
    try {
      settingMessage = await getSetting({
        column: "greetingAcceptedMessage",
      });
    } catch (err) {
      if (isMounted.current) toastError(err);
      return;
    }

    if (
      !settingMessage ||
      !settingMessage.greetingAcceptedMessage ||
      settingMessage.greetingAcceptedMessage.trim() === ""
    ) {
      if (isMounted.current) {
        toastError("Mensagem de saudação não configurada");
      }
      return;
    }

    if (
      (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled") &&
      ticket.status === "pending"
    ) {
      let msg = settingMessage.greetingAcceptedMessage;

      if (msg.trim() === "") {
        return;
      }
      const message = {
        read: 1,
        fromMe: true,
        mediaUrl: "",
        body: `${msg.trim()}`,
      };
      try {
        await api.post(`/messages/${id}`, message);
      } catch (err) {
        if (isMounted.current) toastError(err);
      }
    }
  };

  const handleUpdateTicketStatus = async (queueId) => {
    if (!isMounted.current) return;
    setLoading(true);

    try {
      const otherTicket = await api.put(`/tickets/${ticketId}`, {
        status:
          ticket.isGroup && ticket.channel === "whatsapp" ? "group" : "open",
        userId: user?.id || null,
        queueId: queueId,
        ticketTabId: selectedTicketTab || undefined,
      });

      // ✅ FIX: Verificar se foi redirecionado para ticket existente
      if (otherTicket.data.redirectedToExisting) {
        toast.info(otherTicket.data.redirectMessage || "Já existe um atendimento aberto para este contato. Você foi redirecionado.");
      }

      const targetUuid = otherTicket.data.uuid || ticket.uuid;
      const newTabValue =
        otherTicket.data.isGroup || ticket.isGroup ? "group" : "open";

      // ⚠️ CRÍTICO: Verificar se ticket pertence a outro usuário ANTES de fechar modal
      if (otherTicket.data.id !== ticket.id && otherTicket.data.userId !== user?.id) {
        // Ticket pertence a outro usuário - mostrar alerta sem fechar modal
        setOpenAlert(true);
        setUserTicketOpen(otherTicket.data.user?.name || "Outro usuário");
        setQueueTicketOpen(otherTicket.data.queue?.name || "");
        setLoading(false);
        return;
      }

      // ✅ Chamar callback
      if (typeof onTicketAccepted === "function") {
        onTicketAccepted(newTabValue);
      }

      // ✅ Enviar mensagem de saudação se for o mesmo ticket
      if (otherTicket.data.id === ticket.id) {
        await handleSendMessage(ticket.id);
      }

      // ✅ Fechar modal ANTES da navegação
      onClose();
      setSelectedQueue("");

      // ✅ ORDEM CORRETA: 1. Mudar aba → 2. Definir ticket → 3. Navegação automática via useEffect
      setTabOpen(newTabValue);
      
      // ✅ setCurrentTicket dispara history.push via useEffect no TicketsContext
      setCurrentTicket({
        id: otherTicket.data.id,
        uuid: targetUuid,
        code: "#" + newTabValue,
      });

      setLoading(false);
    } catch (err) {
      if (isMounted.current) {
        setLoading(false);
        toastError(err);
      }
    }
  };

  return (
    <>
      <StandardModal
        open={modalOpen}
        onClose={handleClose}
        title={i18n.t("ticketsList.acceptModal.title")}
        maxWidth="xs"
        cancelText={i18n.t("ticketsList.buttons.cancel")}
        confirmationText={i18n.t("ticketsList.buttons.start")}
        onConfirm={() => handleUpdateTicketStatus(selectedQueue)}
        disableConfirmButton={selectedQueue === ""}
        loading={loading}
        useButtonSpinner={true}
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel className={classes.inputLabel}>
              {i18n.t("ticketsList.acceptModal.queue")}
            </InputLabel>
            <Select
              value={selectedQueue}
              onChange={(e) => {
                e.stopPropagation();
                setSelectedQueue(e.target.value);
              }}
              label={i18n.t("ticketsList.acceptModal.queue")}
              className={classes.selectMenu}
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                getContentAnchorEl: null,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuItem value={""} className={classes.menuItem}>
                &nbsp;
              </MenuItem>
              {user.queues.map((queue) => (
                <MenuItem
                  key={queue.id}
                  value={queue.id}
                  className={classes.menuItem}
                >
                  {queue.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {ticketTabs.length > 1 && (
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel className={classes.inputLabel}>
                Aba de Atendimento
              </InputLabel>
              <Select
                value={selectedTicketTab}
                onChange={(e) => {
                  e.stopPropagation();
                  setSelectedTicketTab(e.target.value);
                }}
                label="Aba de Atendimento"
                className={classes.selectMenu}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {ticketTabs.map((tab) => (
                  <MenuItem
                    key={tab.id}
                    value={tab.id}
                    className={classes.menuItem}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: tab.color,
                          flexShrink: 0,
                        }}
                      />
                      {tab.name}
                      {tab.useStarredSound && (
                        <span style={{ fontSize: "0.7rem", color: "#f9a825" }}>★</span>
                      )}
                    </span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>
      </StandardModal>
      <ShowTicketOpen
        isOpen={openAlert}
        handleClose={handleCloseAlert}
        user={userTicketOpen}
        queue={queueTicketOpen}
      />
    </>
  );
};

export default AcceptTicketWithouSelectQueue;
